﻿import React, { createContext, useContext, useMemo } from "react";
import {
  ActivityDashboardItem,
  WeekReport,
  HrmLoanStatus,
} from "./ActivitiesTypes";
import {
  bandLabel,
  buildSparkPath,
  formatDurationFromHours,
  formatHeaderDate,
  gradientClass,
  makeTrailing7,
  getWeeklyScoreZone,
  getMonotonyLabel,
  getStrainLabel,
} from "./utils";

export interface ActivitiesContextValue {
  activities: ActivityDashboardItem[];
  dailyLoads: number[];
  weekReport: WeekReport | null;
  hrmLoanStatus: HrmLoanStatus | null;
  trailingDays: { date: Date; key: string; label: string }[];
  maxDaily: number;
  totalSessionsThisWeek: number;
  loading: boolean;
  error: string | null;
  /** Optional URL template for activity links. Supports placeholders: {activityGuid}, {userId}, {participantId} */
  linkUrlTemplate?: string | null;
  helpers: {
    bandLabel: typeof bandLabel;
    buildSparkPath: typeof buildSparkPath;
    formatDurationFromHours: typeof formatDurationFromHours;
    formatHeaderDate: typeof formatHeaderDate;
    gradientClass: typeof gradientClass;
    getWeeklyScoreZone: typeof getWeeklyScoreZone;
    getMonotonyLabel: typeof getMonotonyLabel;
    getStrainLabel: typeof getStrainLabel;
  };
}

const ActivitiesDataContext = createContext<ActivitiesContextValue | null>(null);

export function ActivitiesDataProvider(props: {
  activities: ActivityDashboardItem[];
  dailyLoads: number[];
  weekReport: WeekReport | null;
  hrmLoanStatus?: HrmLoanStatus | null;
  loading: boolean;
  error: string | null;
  /** Optional URL template for activity links. Supports placeholders: {activityGuid}, {userId}, {participantId} */
  linkUrlTemplate?: string | null;
  children: React.ReactNode;
}) {
  const trailingDays = useMemo(() => makeTrailing7(), []);
  const maxDaily = useMemo(
    () => Math.max(1, ...(props.dailyLoads || [1])),
    [props.dailyLoads]
  );
  const totalSessionsThisWeek = useMemo(() => {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return (props.activities || []).filter((a) => {
      const d = a._trainDate;
      const last = a._lastModified;
      const effective = d ?? last;
      return !!effective && effective >= sevenDaysAgo && effective <= now;
    }).length;
  }, [props.activities]);

  const value: ActivitiesContextValue = {
    activities: props.activities || [],
    dailyLoads: props.dailyLoads || [0, 0, 0, 0, 0, 0, 0],
    weekReport: props.weekReport ?? null,
    hrmLoanStatus: props.hrmLoanStatus ?? null,
    trailingDays,
    maxDaily,
    totalSessionsThisWeek,
    loading: !!props.loading,
    error: props.error ?? null,
    linkUrlTemplate: props.linkUrlTemplate ?? null,
    helpers: {
      bandLabel,
      buildSparkPath,
      formatDurationFromHours,
      formatHeaderDate,
      gradientClass,
      getWeeklyScoreZone,
      getMonotonyLabel,
      getStrainLabel,
    },
  };

  return (
    <ActivitiesDataContext.Provider value={value}>
      {props.children}
    </ActivitiesDataContext.Provider>
  );
}

export function useActivitiesData(): ActivitiesContextValue {
  const ctx = useContext(ActivitiesDataContext);
  if (!ctx) {
    throw new Error("useActivitiesData must be used within ActivitiesDataProvider");
  }
  return ctx;
}

export interface ActivityItemContextValue {
  item: ActivityDashboardItem;
  index: number;
}

const ActivityItemContext = createContext<ActivityItemContextValue | null>(null);

export function ActivityItemProvider(props: {
  item: ActivityDashboardItem;
  index: number;
  children: React.ReactNode;
}) {
  const value = useMemo(
    () => ({ item: props.item, index: props.index }),
    [props.item, props.index]
  );
  return (
    <ActivityItemContext.Provider value={value}>
      {props.children}
    </ActivityItemContext.Provider>
  );
}

export function useActivityItem(): ActivityItemContextValue {
  const ctx = useContext(ActivityItemContext);
  if (!ctx) {
    throw new Error("useActivityItem must be used within ActivityItemProvider");
  }
  return ctx;
}
